import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import { reviews } from '../../../util/api';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;

  const [reviewsData, setReviewsData] = useState({});
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  const listingsKey = useMemo(
    () => listings.map(l => l.id.uuid).join(','),
    [listings]
  );

  useEffect(() => {
    const fetchReviewSummaries = async () => {
      if (listings.length === 0) return;

      const listingIds = listings.map(l => l.id.uuid);

      const hasAllData = listingIds.every(id => reviewsData[id] !== undefined);
      if (hasAllData) return;

      setIsLoadingReviews(true);
      try {
        const response = await reviews({ listingIds });
        const results = response?.data?.results || response?.results || [];

        const newReviewsData = {};
        results.forEach(result => {
          newReviewsData[result.listingId] = {
            avgRating: result.avgRating || 0,
            reviewCount: result.reviewCount || 0,
          };
        });

        setReviewsData(prev => ({ ...prev, ...newReviewsData }));
      } catch (err) {
        console.error('Failed to fetch review summaries:', err);
      } finally {
        setIsLoadingReviews(false);
      }
    };

    fetchReviewSummaries();
  }, [listingsKey]);

  const listingsWithReviews = useMemo(() => {
    return listings.map(listing => {
      const listingId = listing.id.uuid;
      const reviewData = reviewsData[listingId] || { avgRating: 0, reviewCount: 0 };
      
      return {
        ...listing,
        attributes: {
          ...listing.attributes,
          avgRating: reviewData.avgRating,
          reviewCount: reviewData.reviewCount,
        },
      };
    });
  }, [listings, reviewsData]);

  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listingsWithReviews.map(l => (
          <ListingCard
            className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            isSearchPage={true}
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
          />
        ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;